import React, { useState, useEffect, useRef } from 'react';
import {
  FaSun, // Işık
  FaShieldAlt, // Dayanıklılık
  FaWarehouse, // Yalıtım
  FaBan, // Güvenlik
  FaBullseye, // Anti-Radar
  FaSmile // Kolaylıklar
} from 'react-icons/fa'; // Font Awesome ikonları

// Özellikleri ve detaylı açıklamaları içeren dizi
const ozellikler = [
  {
    icon: FaSun,
    title: 'Işık Geçirgenliği',
    details: [
      '% 70 - % 80 ışık geçirir',
      'UV ışınlarını geçirmez'
    ]
  },
  {
    icon: FaShieldAlt,
    title: 'Yüksek Dayanıklılık',
    details: [
      'İklim koşullarına dayanıklıdır.',
      'Darbelere dayanıklıdır.',
      'Isıyla şekil değiştirmez.',
      'Yüksek rüzgar güvenliği sağlar',
      'Kimyasallara dayanıklıdır.',
      'UV yaşlanmasına dayanıklıdır.',
      'Antikoroziftir (korozyona uğramaz).'
    ]
  },
  {
    icon: FaWarehouse,
    title: 'Isı Yalıtımı',
    details: [
      'Su geçirmez.',
      'Elektrik geçirmez.',
      'Ses yalıtımı sağlar.',
      'Isı yalıtımı sağlar(EN ISO 8990 - 2.02 W/m&sup2;K).',
    ]
  },
  {
    icon: FaBan,
    title: 'Artırılmış Güvenlik',
    details: [
      'EN 13501-1 E2 sertifikalıdır.İsteğe bağlı olarak yanmazlık sınıfı yükseltilebilir.',
      'Yüksek gerilim atlamasını engeller.',
      'Rüzgar, dolu vb. doğa olaylarına çok dirençlidir.',
      'Hafiftir, bina yükünü artırmaz.',
      'Ek deprem güvenliği sağlar.',
      'Ağır metal içermez.',
    ]
  },
  {
    icon: FaBullseye,
    title: 'Anti-Radar Özelliği',
    details: [
      'IEEE 1502-2007.',
      'MILSTD 285.',
      'IEEE 299.'
    ]
  },
  {
    icon: FaSmile,
    title: 'Kolaylık ve Bakım',
    details: [
      'Hafiftir, kolay taşınır.',
      'Montajı kolaydır.',
      'Bakım gerektirmez.',
      'Yatay veya dikey kullanılabilir.'
    ]
  },
];

function OzelliklerSection() {
  const [tooltip, setTooltip] = useState(null);
  const [visibleIcons, setVisibleIcons] = useState({});
  const gridRef = useRef(null);

  const handleMouseEnter = (index) => {
    setTooltip(index);
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  useEffect(() => {
    const currentGridRef = gridRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            ozellikler.forEach((_, index) => {
              setTimeout(() => {
                setVisibleIcons((prev) => ({ ...prev, [index]: true }));
              }, index * 200);
            });
            if (currentGridRef) {
                observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        threshold: 0.2,
      }
    );

    if (currentGridRef) {
      observer.observe(currentGridRef);
    }

    return () => {
      if (currentGridRef) {
        observer.unobserve(currentGridRef);
      }
    };
  }, []);

  return (
    <div
      className="relative min-h-[60vh] flex flex-col items-center justify-center p-6 text-white overflow-hidden"
      style={{
        backgroundImage: "url('/parallax-technical-6829.jpg')",
        backgroundAttachment: 'fixed',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>

      <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-screen-lg mx-auto">
        <header className="text-center mt-16">
          <h2 className="text-3xl font-light tracking-wide drop-shadow-lg leading-tight">
            Teknik Özellikler
          </h2>
          <div className="w-20 h-0.5 bg-white mt-2 mb-8 mx-auto opacity-75 rounded-full"></div>
        </header>

        <div className="max-w-3xl text-center mb-16">
          <p className="text-lg leading-relaxed font-extralight opacity-95 drop-shadow-lg">
            Panolux ışık geçiren CTP paneller, uçak hangarlarından kış bahçelerine kadar
            sınırsız kullanıma olanak sağlayan yenilikçi teknik özelliklere sahiptir.
          </p>
        </div>

        <div
          ref={gridRef}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-8 w-full px-4"
        >
          {ozellikler.map((ozellik, index) => (
            <div
              key={index}
              className={`flex flex-col items-center text-center relative group p-2
                          transform transition-all duration-1000 ease-out
                          ${visibleIcons[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className="relative flex items-center justify-center w-16 h-16 rounded-full border border-white border-opacity-60 mb-2
                                transform transition-all duration-300 ease-in-out
                                group-hover:scale-105 group-hover:border-opacity-100 group-hover:shadow-md">
                {/* Apply the rotation directly to the icon element */}
                <ozellik.icon
                  className="text-white text-3xl opacity-80 transition-all duration-500 ease-in-out group-hover:opacity-100 group-hover:rotate-[360deg]"
                />
              </div>
              <h3 className="text-md font-light tracking-wide drop-shadow-lg mb-1">
                {ozellik.title}
              </h3>

              {tooltip === index && (
                <div className="absolute top-[-10px] left-1/2 transform -translate-x-1/2 -translate-y-full
                                  bg-gradient-to-t from-emerald-800/80 to-emerald-900/80 text-white text-xs
                                  rounded-md py-2 px-3 shadow-md opacity-0 scale-90
                                  transition-all duration-300 ease-out z-20
                                  group-hover:opacity-100 group-hover:scale-100 w-48 md:w-56 lg:w-64
                                  pointer-events-none border border-emerald-700">
                  <ul className="list-disc list-inside text-left space-y-0.5 font-extralight">
                    {ozellik.details.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0
                                   border-l-4 border-l-transparent border-r-4 border-r-transparent
                                   border-t-4 border-t-gray-800"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OzelliklerSection;